import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable, catchError, of, switchMap, throwError } from "rxjs";

import { LocalStorageService, SessionStorageService } from "ngx-webstorage";

import {Router} from "@angular/router";
import {AuthService} from "@shared/services/auth.service";
import {ToasterService} from "@shared/services/toaster.service";
import {ExamineeAuthService} from "@shared/services/examinee-auth.service";
import {InstallationService} from "@shared/services/installation.service";
import {LoadingService} from "@shared/services/loading.service";
import {AndroidService} from "@shared/services/android.service";
import {getAccessTokenCookie} from "@shared/utils/cookie.utils";

@Injectable()
export class HttpRequestsInterceptor implements HttpInterceptor {
    public readonly EXAMINEE_ERR_THROW_PLATFORMS: string[] = [
        "collection"
    ]

    constructor(
        @Inject('environment') private _environment: any,
        private _toasterService: ToasterService,
        private _localStorageService: LocalStorageService,
        private _sessionStorageService: SessionStorageService,
        private _installationService: InstallationService,
        private _router: Router,
        private _authService: AuthService,
        private _loaderService: LoadingService,
        private _examineeAuthService: ExamineeAuthService,
        private _androidService: AndroidService
    ) {

    }

    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        if (req.headers.get("skip")) {
            return next.handle(req);
        }


        if (this._examineeAuthService.examineeToken) {
            req = this._addExamineeToken(req, this._examineeAuthService.examineeToken);
        }

        if (this._localStorageService.retrieve('session_token') || this._sessionStorageService.retrieve('session_token')) {
            req = this._addSessionToken(req, this._localStorageService.retrieve('session_token') || this._sessionStorageService.retrieve('session_token'));
        }

        if (this._localStorageService.retrieve('doctor_view_session_token')) {
            req = this._addDoctorViewSessionToken(req, this._localStorageService.retrieve('doctor_view_session_token'));
        }

        const accessToken = getAccessTokenCookie();

        if (accessToken) {
            req = this._addAccessToken(req, accessToken);
        }

        if (this._localStorageService.retrieve('installation_id')) {
            req = this._addInstallationId(req, this._localStorageService.retrieve('installation_id'));
        }

        if (this._localStorageService.retrieve('lng') || this._localStorageService.retrieve('default-lng')) {
            const lng = this._localStorageService.retrieve('lng') || this._localStorageService.retrieve('default-lng');

            req = this._addLanguage(req, lng);
        }


        if (this._androidService.isAndroid()) {
            req = this._addAndroidVersion(req);
        }

        const Req: HttpRequest<any> = req.clone({url: this._environment.serverUrl + req.url});

        return next.handle(Req).pipe(
            catchError((err) => {
                this._loaderService.loading.set(false);

                // Unauthorized - LIS_SESSION_TOKEN
                if (err.status === 401) {

                    const redirectUrl: string = window.location.pathname;

                    if (redirectUrl.includes('doctor-view')) {
                        this._authService.logout(window.location.href);
                    } else {
                        this._authService.logout(redirectUrl);
                    }

                    return of(err);
                }

                // Missing installation ID
                if (err.status === 435) {
                    this._installationService.createInstallationIfNotExists(true);
                    return this._installationService.installationIdReady.pipe(
                        switchMap(() => next.handle(Req))
                    );
                }
                // Issue with access to lab and platform - LIS_LAB_ACCESS_TOKEN
                if (err.status === 437) {
                    this._authService.resetAccess();

                    return of(err);
                }

                // Issue with examinee authentication - LIS_EXAMINEE_TOKEN
                if (err.status === 438 && this.EXAMINEE_ERR_THROW_PLATFORMS.includes(this._environment?.slug)) {
                    // In such case, we need to throw the error so we can catch it in the component and display a pin dialog
                    throw err;
                }

                // USER_WITHOUT_PERMISSION
                if (err.status === 403) {
                    this._router.navigate(['/']);

                    this._toasterService.warning("Warning", "You don't have permission to access this resource.");

                    return of(err);
                }

                // Do not print every error toasts - let the services/components handle it
                // this._toasterService.error("Error", err.error?.message || "Something went wrong");

                return throwError(() => err);

            })
        );
    }

    private _addExamineeToken(request: HttpRequest<any>, examineeToken: string) {
        return request.clone({
            setHeaders: {
                'x-examinee-token': examineeToken
            }
        });
    }

    private _addSessionToken(request: HttpRequest<any>, sessionToken: string) {

        return request.clone({
            setHeaders: {
                'x-session-token': sessionToken
            }
        });
    }

    private _addDoctorViewSessionToken(request: HttpRequest<any>, sessionToken: string) {

        return request.clone({
            setHeaders: {
                'x-doctor-view-session-token': sessionToken
            }
        });
    }

    private _addInstallationId(request: HttpRequest<any>, installationId: string) {
        return request.clone({
            setHeaders: {
                'x-installation-id': installationId
            }
        });
    }

    private _addAccessToken(request: HttpRequest<any>, accessToken: string) {
        return request.clone({
            setHeaders: {
                'x-access-token': accessToken
            }
        });
    }

    private _addLanguage(request: HttpRequest<any>, language: string) {
        return request.clone({
            setHeaders: {
                'x-language': language.split('-')[0]
            }
        });
    }

    private _addAndroidVersion(request: HttpRequest<any>) {
        const androidVersion  = this._androidService.version;
        if (!androidVersion.androidVersion || !androidVersion.appVersion) {
            return request;
        }
        return request.clone({
            setHeaders: {
                'x-android-app-version': androidVersion.appVersion,
                'x-android-version': androidVersion.androidVersion
            }
        });
    }
}
